import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getService } from "../../../reducers/service";
import {
  formatDateToMonthDateYear,
  formatSSN,
  mapDictionaryOptions,
} from "../../../utils/functions";
import { setCircularLoading } from "../../../reducers/UiReducer";
import ApplicantFilters from "./ApplicantFilters";
import { notify } from "../../../reducers/NotifierReducer";
import { createEmployee } from "../../../reducers/EmployeeReducer";
import { updateOrCreateUserSetting } from "../../../reducers/UserSettingsReducer";
import CustomTable from "../../../components/CustomTable";
import { entityNamesForCustomId, serviceNames } from "../../../utils/constants";
import PATSaveButton from "../../../components/Buttons/PATSaveButton";
import PATCloseButton from "../../../components/Buttons/PATCloseButton";
import {
  getPhotosByEntityId,
  updatePhoto,
} from "../../../reducers/PersonnelPhotosReducer";
import { PerPhotoType } from "../../../utils/enums";
import moment from "moment";

const tableHeaders = ["Name", "DOB", "Recruit SSN"];

const ApplicantDialog = ({ open, onClose }) => {
  const dispatch = useDispatch();
  const userAgency = useSelector((state) => state.user.userAgency);

  const { userData } = useSelector((state) => state.user);
  const { userSettings } = useSelector((state) => state.userSettings);

  const [status, setStatus] = useState(null);
  const [applicants, setApplicants] = useState([]);
  const [applicationYear, setApplicationYear] = useState(null);
  const [yearOptions, setYearOptions] = useState([]);
  const [firstName, setFirstName] = useState("");
  const [middleName, setMiddleName] = useState("");
  const [lastName, setLastName] = useState("");
  const [ssn, setSSN] = useState("");
  const [selectedApplicants, setSelectedApplicants] = useState([]);
  const [defaultEmploymentStatus, setDefaultEmploymentStatus] = useState(null);
  const dictionary = useSelector((state) => state.dictionary.dictionary);
  const statusOptions = mapDictionaryOptions(dictionary.codePerApplicantStatus);

  const fetchOptions = async () => {
    try {
      dispatch(setCircularLoading(true));
      const yearOptionsService = getService("per-applicant/year-options");

      const [yearOptionsResult] = await Promise.all([
        yearOptionsService.find(),
      ]);

      setYearOptions(
        (yearOptionsResult || []).map((year) => ({
          label: String(year),
          value: year,
        }))
      );
    } catch (error) {
      console.error("Error fetching options:", error);
    } finally {
      dispatch(setCircularLoading(false));
    }
  };

  const fetchApplicants = async () => {
    try {
      dispatch(setCircularLoading(true));
      const applicantService = getService("per-applicant");
      const employeeService = getService("personnel");

      const query = {
        $limit: -1,
        isDeleted: false,
      };

      if (applicationYear?.value) {
        query.yearApplied = applicationYear.value;
      }

      if (status) {
        query.status = status;
      }

      if (firstName.trim()) {
        query.firstName = { $like: `%${firstName.trim().toLowerCase()}%` };
      }

      if (middleName.trim()) {
        query.middleName = { $like: `%${middleName.trim().toLowerCase()}%` };
      }

      if (lastName.trim()) {
        query.lastName = { $like: `%${lastName.trim().toLowerCase()}%` };
      }

      if (ssn.trim()) {
        query.ssn = { $like: `%${ssn.trim().toLowerCase()}%` };
      }

      const [applicantResult, employeeResult] = await Promise.all([
        applicantService.find({ query }),
        employeeService.find({ query: { $limit: -1, isDeleted: false } }),
      ]);

      const employees = employeeResult || [];
      const applicants = applicantResult || [];

      const employeeSSNs = new Set(employees.map((emp) => emp.ssn));

      const filteredApplicants = applicants.filter(
        (applicant) => !employeeSSNs.has(applicant.ssn)
      );

      setApplicants(filteredApplicants);
    } catch (error) {
      console.error("Error fetching applicants:", error);
    } finally {
      dispatch(setCircularLoading(false));
    }
  };

  const fetchEmploymentStatus = async () => {
    const employmentStatusService = getService("code-per-employment-status");
    const result = await employmentStatusService.find({
      query: { $limit: -1, isDeleted: false, agencyId: userAgency },
    });
    const defaultRecord = result.find((item) => item.isDefault);
    if (defaultRecord && defaultRecord.code) {
      setDefaultEmploymentStatus(defaultRecord.code);
    }
  };

  useEffect(() => {
    if (open) {
      fetchOptions();
      fetchEmploymentStatus();

      const settingsMap = Object.fromEntries(
        userSettings.map((s) => [s.key, s.value])
      );
      if (settingsMap["applicantDialogFilters"]) {
        const stored = settingsMap["applicantDialogFilters"];
        try {
          const {
            applicationYear,
            status,
            firstName,
            middleName,
            lastName,
            ssn,
          } = JSON.parse(stored);

          if (applicationYear) setApplicationYear(applicationYear);
          if (status) setStatus(status);
          if (firstName) setFirstName(firstName);
          if (middleName) setMiddleName(middleName);
          if (lastName) setLastName(lastName);
          if (ssn) setSSN(ssn);
        } catch (err) {
          console.error("Failed to load saved filters", err);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, userAgency]);

  useEffect(() => {
    if (!open) return;

    const handler = setTimeout(() => {
      fetchApplicants();
    }, 500);

    return () => {
      clearTimeout(handler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationYear, status, firstName, middleName, lastName, ssn, open]);

  const generateCustomId = async () => {
    const data = {
      EntityName: entityNamesForCustomId.personnelEmployeeId,
      agencyID: userAgency,
    };
    const service = getService(serviceNames.customId);
    service.timeout = 20000;

    const id = await service.create(data);
    return id;
  };

  const persistFilters = () => {
    if (userData?.user?.ptsUserID) {
      dispatch(
        updateOrCreateUserSetting(
          userData.user.ptsUserID,
          "applicantDialogFilters",
          JSON.stringify({
            applicationYear,
            status,
            firstName,
            middleName,
            lastName,
            ssn,
          })
        )
      );
    }
  };

  const toggleApplicantSelection = (applicant) => {
    setSelectedApplicants((prev) => {
      const isAlreadySelected = prev.some((a) => a.ssn === applicant.ssn);
      if (isAlreadySelected) {
        return prev.filter((a) => a.ssn !== applicant.ssn);
      } else {
        return [...prev, applicant];
      }
    });
  };

  const createFromSelected = async () => {
    if (selectedApplicants.length === 0) {
      dispatch(
        notify(
          "No applicant selected. Please select at least one applicant.",
          "warning"
        )
      );
      return;
    }

    persistFilters();

    try {
      const generatedId = await generateCustomId();
      if (generatedId?.includes("Agency ID does not match")) {
        dispatch(
          notify(
            "Unable to create employee record. No custom Employee ID has been configured for this agency.",
            "warning"
          )
        );
        return;
      }
      await Promise.all(
        selectedApplicants.map(async (applicant) => {
          const empPayload = {
            lastName: applicant.lastName ?? null,
            firstName: applicant.firstName ?? null,
            middleName: applicant.middleName ?? null,
            dob: applicant?.dob ? moment(applicant.dob).startOf("day").format("YYYY-MM-DD HH:mm:ss.SSS") : null,
            ssn: applicant.ssn ?? null,
            gender: applicant.gender ?? null,
            maritalStatus: applicant.maritalStatus ?? null,
            address: applicant.address ?? null,
            rCity: applicant.city ?? null,
            rState: applicant.state ?? null,
            rZip: applicant.zip ?? null,
            phoneNo: applicant.phone ?? null,
            medicalNotes: applicant.medicalNotes ?? null,
            education: applicant.education ?? null,
            militaryNotes: applicant.militaryRelatedNotes ?? null,
            email: applicant.email ?? null,
            suffix: applicant.suffix ?? null,
            workPhoneNum: applicant.workPhoneNum ?? null,
            status: defaultEmploymentStatus,
            employeeNum: generatedId ?? null,
            ptsPersonId: applicant.ptsPersonId ?? null,
            race: applicant.race ?? null,
            agencyId: userAgency,
          };

          const newEmployee = await dispatch(createEmployee(empPayload));

          const applicantPhotosResp = await dispatch(
            getPhotosByEntityId(applicant.id, PerPhotoType.Applicant)
          );

          const applicantPhotos = applicantPhotosResp?.photos;

          if (applicantPhotos.length > 0) {
            await Promise.all(
              applicantPhotos.map((photo) =>
                dispatch(
                  updatePhoto(
                    photo.imageID,
                    newEmployee.id,
                    PerPhotoType.Employee,
                    {
                      entityId: newEmployee.id,
                      photoFor: PerPhotoType.Employee,
                    },
                    false
                  )
                )
              )
            );
          }
        })
      );

      handleDialogClose();
    } catch (error) {
      console.error("Batch creation failed:", error);
      dispatch(notify("An error occurred while creating employees.", "error"));
    }
  };

  const handleDialogClose = () => {
    persistFilters();
    setSelectedApplicants([]);
    setApplicants([]);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleDialogClose} maxWidth="lg" fullWidth>
      <DialogTitle>Applicants With No Employee File</DialogTitle>
      <DialogContent>
        <ApplicantFilters
          yearOptions={yearOptions}
          applicationYear={applicationYear}
          setApplicationYear={setApplicationYear}
          statusOptions={statusOptions}
          status={status}
          setStatus={setStatus}
          firstName={firstName}
          setFirstName={setFirstName}
          middleName={middleName}
          setMiddleName={setMiddleName}
          lastName={lastName}
          setLastName={setLastName}
          ssn={ssn}
          setSSN={setSSN}
        />

        <CustomTable
          columns={tableHeaders}
          data={applicants.map((applicant) => ({
            Name: `${applicant.lastName}, ${applicant.firstName}${
              applicant.middleName ? ` ${applicant.middleName}` : ""
            }`,
            DOB: applicant.dob ? formatDateToMonthDateYear(applicant.dob) : "",
            "Recruit SSN": formatSSN(applicant.ssn),
            _raw: applicant,
          }))}
          getRowKey={(row) => row._raw.ssn}
          isRowSelected={(row) =>
            selectedApplicants.some((a) => a.ssn === row._raw.ssn)
          }
          onCheckboxToggle={(row) => toggleApplicantSelection(row._raw)}
          showCheckbox={true}
          isAllSelected={
            applicants.length > 0 &&
            selectedApplicants.length === applicants.length
          }
          onToggleAll={() =>
            selectedApplicants.length === applicants.length
              ? setSelectedApplicants([])
              : setSelectedApplicants(applicants)
          }
        />
      </DialogContent>
      <DialogActions>
        <PATSaveButton
          variant="contained"
          color="primary"
          tooltipText="Create From Selected"
          onClick={() => createFromSelected()}
        />
        <PATCloseButton
          tooltipText="Close"
          onClick={() => handleDialogClose()}
        />
      </DialogActions>
    </Dialog>
  );
};

export default ApplicantDialog;
